import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Calendar } from '../../components/ui/calendar';
import { attendanceApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Calendar as CalendarIcon, CheckCircle, XCircle, Clock } from 'lucide-react';

const StudentAttendancePage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attendanceDates, setAttendanceDates] = useState({ present: [], absent: [], late: [] });

  useEffect(() => {
    loadAttendance();
  }, []);

  const loadAttendance = async () => {
    try {
      const response = await attendanceApi.getStats(user?.student_id);
      setStats(response.data);
      
      // Map dates for calendar
      const present = [];
      const absent = [];
      const late = [];
      
      (response.data.records || []).forEach(record => {
        const date = new Date(record.date);
        if (record.status === 'present') present.push(date);
        else if (record.status === 'absent') absent.push(date);
        else if (record.status === 'late') late.push(date);
      });
      
      setAttendanceDates({ present, absent, late });
    } catch (error) {
      console.error('Failed to load attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-8 w-8 text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="student-attendance-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold">My Attendance</h1>
        <p className="text-muted-foreground mt-1">Track your attendance record</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Attendance Rate</p>
                <p className={`text-3xl font-bold ${(stats?.percentage || 0) >= 75 ? 'text-green-500' : 'text-red-500'}`}>
                  {stats?.percentage || 0}%
                </p>
              </div>
              <div className="p-3 rounded-lg bg-primary/10">
                <CalendarIcon className="h-6 w-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Present Days</p>
                <p className="text-3xl font-bold text-green-500">{stats?.present || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-green-500/10">
                <CheckCircle className="h-6 w-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Absent Days</p>
                <p className="text-3xl font-bold text-red-500">{stats?.absent || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-red-500/10">
                <XCircle className="h-6 w-6 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Late Days</p>
                <p className="text-3xl font-bold text-yellow-500">{stats?.late || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-yellow-500/10">
                <Clock className="h-6 w-6 text-yellow-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calendar View */}
      <Card className="border border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-primary" />
            Attendance Calendar
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center">
            <Calendar
              mode="multiple"
              selected={[...attendanceDates.present, ...attendanceDates.absent, ...attendanceDates.late]}
              className="rounded-md border"
              modifiers={{
                present: attendanceDates.present,
                absent: attendanceDates.absent,
                late: attendanceDates.late
              }}
              modifiersStyles={{
                present: { backgroundColor: '#22C55E', color: 'white', borderRadius: '50%' },
                absent: { backgroundColor: '#EF4444', color: 'white', borderRadius: '50%' },
                late: { backgroundColor: '#EAB308', color: 'white', borderRadius: '50%' }
              }}
            />
          </div>
          <div className="flex justify-center gap-6 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-green-500" />
              <span className="text-sm">Present</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-red-500" />
              <span className="text-sm">Absent</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded-full bg-yellow-500" />
              <span className="text-sm">Late</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentAttendancePage;
